/* eslint-disable no-console */
/* eslint-disable camelcase */

import { colors } from '@lmc/common/constants'
import type {
  Carer,
  ConsentForm,
  Home,
  LogCategoryItem,
  Resident
} from '@lmc/ts-common'
import { LabelIcon, NextReviewField, PageHeader } from '@lmc/ui-kit'
import { Form, Row } from 'antd'
import dayjs from 'dayjs'
import type * as React from 'react'
import {
  type CSSProperties,
  type ReactElement,
  useEffect,
  useState
} from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import PdfExport from '../../../../../components/pdf/PdfExport'
import { history } from '../../../../../store'
import { getTimeOptions, onSubmit, setNextReviewDateByOptions } from '../utils'
import {
  ConsentFormTextBox,
  LastReviewCard,
  LogsSection,
  NextReviewCard,
  ReviewFooter
} from './components'
import PDFHeader from './PDFScreen/PDFHeader'

type ConsentFormScreenProps = {
  fetchResidentConsentFormByID: (id: string) => void
  consentForm: ConsentForm
  home: Home
  printPDF?: boolean
  resident: Resident
  router: any
  logs: any
  lastReviewCarer?: Carer
  signatureItem: LogCategoryItem
  updateConsent: ({
    isRefused,
    comment,
    editedBy,
    editedOn,
    nextReviewDate,
    consentForm
  }: any) => Promise<any>
}

export const ConsentFormScreen = ({
  consentForm,
  fetchResidentConsentFormByID,
  home,
  printPDF,
  lastReviewCarer,
  logs,
  resident,
  router,
  signatureItem,
  updateConsent
}: ConsentFormScreenProps): ReactElement => {
  const { t } = useTranslation()
  const translate = (key: string, variable?: any) =>
    t(`consent.createConsentForms.components.consentModal.${key}`, variable)

  const labelTextBoxTranslate = (key: string, variable?: any) =>
    t(`components.labelTextBox.${key}`, variable)

  const [isReviewing, setIsReviewing] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [nextReviewDate, setNextReviewDate] = useState<string | undefined>()
  const [additionalInfo, setAdditionalInfo] = useState<string | undefined>()
  const [timeOptionNextReview, setTimeOptionNextReview] = useState<
    string | undefined
  >()

  const navigate = useNavigate()

  const [form] = Form.useForm()

  const consentFormIdFromURL = router.params.consent_form_id

  useEffect(() => {
    fetchResidentConsentFormByID(consentFormIdFromURL)
  }, [consentFormIdFromURL])

  useEffect(() => {
    // @ts-ignore
    setNextReviewDateByOptions(timeOptionNextReview, setNextReviewDate, form)
  }, [timeOptionNextReview])

  const onSubmitSign = async ({ formState, onSuccess }: any) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      await onSubmit({
        resident,
        nextReviewDate,
        formState,
        consentSingleForm: consentForm,
        isSingleSign: true,
        item: signatureItem.id,
        category: signatureItem.category
      })
      setIsSubmitting(false)
      onSuccess()
      fetchResidentConsentFormByID(consentFormIdFromURL)
    } catch {
      setIsSubmitting(false)
    }
  }

  const handleSave = async () => {
    try {
      const res = await updateConsent({
        isRefused: false,
        comment: additionalInfo,
        editedBy: resident.id,
        editedOn: dayjs(new Date()).toISOString(),
        nextReviewDate: dayjs(nextReviewDate, 'DD-MM-YYYY').toISOString(),
        consentForm
      })
      if (res) {
        handleCancel()
      }
    } catch (e) {
      console.log(e)
    }
  }

  const handleCancel = () => {
    setIsReviewing(false)
    setNextReviewDate(undefined)
    setAdditionalInfo(undefined)
  }

  const handleDateChange = (date: any, dateString: any) => {
    if (date !== null) {
      setNextReviewDate(dateString)
    } else {
      setTimeOptionNextReview(undefined)
      setNextReviewDate(undefined)
    }
  }

  const isDesktop = isBrowser || isTablet

  // @ts-ignore
  if (!consentForm || !resident) return null

  const pdfUrl = `residents/${resident?.id}/pdf/consent/${consentForm?.id}`
  const url = `/api/services/pdf/export?url=${encodeURIComponent(pdfUrl)}`

  return (
    <div style={printPDF ? styles.pdfWrapper : styles.wrapper}>
      <Row style={styles.mainRow as CSSProperties}>
        <Row>{printPDF && <PDFHeader home={home} resident={resident} />}</Row>
        <Row>
          <div style={styles.header}>
            <PageHeader
              sticky
              // @ts-ignore
              backLink={!printPDF}
              onBack={() => navigate('../', { relative: 'path' })}
              // @ts-ignore
              rightChildren={
                !printPDF && (
                  <PdfExport
                    pdfTitle={`${resident?.name?.first} ${resident?.name?.last}'s ${consentForm?.title}`}
                    url={url}
                    text={
                      isDesktop
                        ? translate('downloadPDF')
                        : translate('downloadPDFMobile')
                    }
                  />
                )
              }
              title={consentForm?.title}
              onClick={history.back}
            />
          </div>
          <LastReviewCard
            consentForm={consentForm}
            // @ts-ignore
            lastReviewCarer={lastReviewCarer}
            style={styles.reviewCard}
            translate={translate}
          />
          <ConsentFormTextBox
            consentForm={consentForm}
            localisation={{
              // @ts-ignore
              title: consentForm?.title,
              subTitle: translate('statement', {
                variable: resident?.preferredName || resident?.name?.first
              }),
              readOnly: labelTextBoxTranslate('readOnly')
            }}
            resident={resident}
          />
        </Row>
        {isReviewing ? (
          <div style={styles.additionalComments}>
            <Form form={form}>
              <NextReviewField
                localisation={{
                  title: 'Next review',
                  additionalInfo: 'Any additional comments on this review?'
                }}
                dateValue={nextReviewDate}
                datePickerOnChange={handleDateChange}
                radioGroupOnChange={setTimeOptionNextReview}
                timeValue={timeOptionNextReview}
                textAreaOnChange={(e: any) => setAdditionalInfo(e.target.value)}
                additionalInfo
                additionalInfoValue={additionalInfo}
                timeOptions={getTimeOptions(translate)}
                isMobile={!isDesktop}
              />
            </Form>
          </div>
        ) : (
          <NextReviewCard
            nextReviewDate={
              (consentForm?.nextReviewDate || nextReviewDate) as string
            }
            // @ts-ignore
            onReview={!printPDF && (() => setIsReviewing(true))}
            style={styles.reviewCard}
            translate={translate}
          />
        )}
        <LogsSection
          consentForm={consentForm}
          isSubmitting={isSubmitting}
          logs={logs}
          // @ts-ignore
          onSubmitSign={!printPDF && onSubmitSign}
          resident={resident}
          translate={translate}
        />
        <br />
      </Row>
      {!printPDF && (
        <Row>
          <LabelIcon
            iconColor={colors.pink}
            iconName="QuestionCircleOutlined"
            onPress={() =>
              window.open(
                'http://support.logmycare.co.uk/en/articles/6014582-how-do-i-review-a-consent-or-lack-of-capacity-form',
                '_blank'
              )
            }
            text="Help"
            textColor={colors.pink}
          />
        </Row>
      )}
      {isReviewing && (
        <ReviewFooter
          cancelButtonText={t(`general.buttons.cancel`)}
          handleCancel={handleCancel}
          handleSave={handleSave}
          saveButtonText={t(`general.buttons.save`)}
        />
      )}
    </div>
  )
}

const styles = {
  additionalComments: {
    padding: 25,
    marginTop: 50,
    background: colors.white,
    borderRadius: 15
  },
  header: {
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: 10
  } as React.CSSProperties,
  mainRow: {
    flexDirection: 'column'
  },
  reviewCard: {
    marginTop: 30
  },
  wrapper: {
    maxWidth: 1080,
    padding: '0px 0px 100px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  pdfWrapper: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    width: 1190,
    minHeight: 1480,
    zIndex: 1000,
    background: 'white',
    padding: '24px 28px'
  }
}
